import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import {
  getCurrentTeacher,
  toggleFavoriteTool,
} from "@packages/store/userSlice";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { toolsList } from "@packages/utils";
import Modal from "@core/modal";
import { Plus, Search } from "lucide-react";
import { FavoriteButton } from "@features/tools/components/favorite-button";
const AddFavoritesModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const teacherPreferences = useAppSelector(
    (state) => state.user.teacher?.preferences,
  );
  const favorites = teacherPreferences?.favoriteTools || [];

  const allTools = toolsList.flatMap((category) =>
    category.tools.map((tool) => ({
      id: tool.id,
      title: t(tool.title),
      icon: tool.icon,
      color: tool.colors?.light || "from-gray-500 to-gray-600",
      category: t(category.categoryTitle),
      isFavorite: favorites.includes(tool.id),
    })),
  );

  // Filter tools based on search
  const filteredTools = searchTerm
    ? allTools.filter((tool) =>
        tool.title.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : allTools;

  // Group tools by category
  const toolsByCategory = filteredTools.reduce(
    (acc, tool) => {
      if (!acc[tool.category]) {
        acc[tool.category] = [];
      }
      acc[tool.category].push(tool);
      return acc;
    },
    {} as Record<string, typeof filteredTools>,
  );

  const toggleFavorite = async (toolId: string) => {
    await dispatch(toggleFavoriteTool(toolId));
    dispatch(getCurrentTeacher());
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="solid"
        size="sm"
        rounded="full"
        className="flex items-center px-5 space-x-1 text-xs sm:text-sm bg-gray-100 dark:bg-dark-400 hover:bg-gray-200 dark:hover:bg-dark-500"
      >
        <span>{t("quickActions.addToFavorites")}</span>
        <Plus size={16} />
      </Button>

      <Modal
        show={isOpen}
        onClose={() => setIsOpen(false)}
        title={t("quickActions.allTools")}
        outerClassName="max-w-2xl animate-modal-enter"
      >
        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("quickActions.searchTools")}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-dark-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search
              size={18}
              className="absolute right-3 top-2.5 text-gray-400"
            />
          </div>

          {/* Tools List */}
          <div className="max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
            {Object.entries(toolsByCategory).map(([category, tools]) => (
              <div key={category} className="mb-6">
                <Text
                  variant="h4"
                  className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {category}
                </Text>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {tools.map((tool) => (
                    <div
                      key={tool.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div
                          className={`${tool.color} p-2 rounded-lg text-white flex-shrink-0`}
                        >
                          <tool.icon size={18} />
                        </div>
                        <span className="truncate">{tool.title}</span>
                      </div>
                      <FavoriteButton
                        isFavorite={tool.isFavorite}
                        onToggleFavorite={() => toggleFavorite(tool.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AddFavoritesModal;
